import { ChevronRight, Camera, AlertTriangle } from "lucide-react";
import { fmtKm, fmtCZK, fmtDate, STATUS, TIRES, dateTone, photoSrc } from "@/flotila/fleetUtils";

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider fl-muted whitespace-nowrap ${className}`}>{children}</th>
);

const FleetTable = ({ cars, token, isStaff, onOpen, selectable = false, selectedIds, onToggle, onToggleAll }) => {
  if (!cars.length) {
    return <div className="fl-card rounded-2xl p-12 text-center fl-muted" data-testid="fleet-table-empty">Žádné vozy neodpovídají filtru.</div>;
  }
  const allSel = selectable && cars.length > 0 && cars.every((c) => selectedIds?.has(c.id));
  return (
    <div className="fl-card rounded-2xl overflow-hidden" data-testid="fleet-table">
      <div className="overflow-x-auto">
        <table className="min-w-[1180px] w-full text-sm border-separate border-spacing-0">
          <thead className="fl-surface-2">
            <tr>
              {selectable && <Th className="w-8"><input type="checkbox" checked={allSel} onChange={onToggleAll} className="accent-orange-500 cursor-pointer" data-testid="fleet-select-all" aria-label="Vybrat vše" /></Th>}
              <Th className="sticky left-0 fl-sticky z-10">Vůz</Th>
              <Th>SPZ / VIN</Th>
              <Th>Stav</Th>
              <Th>Klient</Th>
              <Th>Nájezd</Th>
              <Th>Tento měsíc</Th>
              <Th>Fotky</Th>
              <Th>Servis</Th>
              <Th>STK</Th>
              <Th>Pojistka</Th>
              <Th>Známka</Th>
              <Th>Pneu</Th>
              {isStaff && <Th>Náklady</Th>}
              {isStaff && <Th>Partner</Th>}
              {!isStaff && <Th>Vaše odměna / měs</Th>}
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {cars.map((c) => {
              const st = STATUS[(c.is_rented || c.client_name) ? "rented" : c.status] || STATUS.free;
              const tm = c.this_month;
              const red = c.alerts?.some((a) => a.level === "red");
              const photos = tm?.photos || [];
              return (
                <tr key={c.id} onClick={() => onOpen(c.id)} className={`group cursor-pointer border-t fl-border fl-row transition-colors ${selectedIds?.has(c.id) ? "fl-accent-soft" : ""}`} data-testid={`fleet-row-${c.id}`}>
                  {selectable && (
                    <td className="px-3 py-2.5 border-t fl-border" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds?.has(c.id) || false} onChange={() => onToggle(c.id)} className="accent-orange-500 cursor-pointer" data-testid={`fleet-select-${c.id}`} aria-label="Vybrat vůz" />
                    </td>
                  )}
                  <td className="sticky left-0 fl-sticky z-10 px-3 py-2.5 border-t fl-border">
                    <div className="flex items-center gap-2">
                      {c.alerts?.length ? <AlertTriangle className={`w-4 h-4 shrink-0 ${red ? "fl-bad" : "fl-warn"}`} /> : <span className="w-4 h-4 shrink-0 rounded-full fl-chip-ok border inline-block" />}
                      <div className="min-w-0">
                        <p className="font-medium fl-text whitespace-nowrap">{c.brand} {c.model}</p>
                        <p className="text-[11px] fl-muted">{c.year || "—"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 border-t fl-border">
                    <p className="font-mono fl-text-2 whitespace-nowrap">{c.license_plate || "—"}</p>
                    <p className="font-mono text-[10px] fl-muted">{c.vin || ""}</p>
                  </td>
                  <td className="px-3 py-2.5 border-t fl-border"><span className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${st.cls}`}>{st.label}</span></td>
                  <td className="px-3 py-2.5 border-t fl-border whitespace-nowrap">
                    <p className="fl-text-2">{c.client_name || (c.is_rented ? "Pronajato" : <span className="fl-muted">—</span>)}</p>
                    {c.rental_until && <p className="text-[10px] fl-muted">do {fmtDate(c.rental_until)}</p>}
                  </td>
                  <td className="px-3 py-2.5 border-t fl-border whitespace-nowrap fl-text-2 font-medium">{fmtKm(c.mileage)}</td>
                  <td className="px-3 py-2.5 border-t fl-border whitespace-nowrap">
                    {tm?.mileage != null ? (
                      <><p className="fl-text-2">{fmtKm(tm.mileage)}</p>{tm.mileage_diff != null && <p className="text-[10px] fl-accent-text">+{Number(tm.mileage_diff).toLocaleString("cs-CZ")} km</p>}</>
                    ) : <span className="text-[11px] px-2 py-0.5 rounded-full fl-chip-warn border">chybí zápis</span>}
                  </td>
                  <td className="px-3 py-2.5 border-t fl-border">
                    <div className="flex items-center gap-1">
                      {photos.slice(0, 3).map((p) => <img key={p.id} src={photoSrc(p.url, token, "thumb")} alt="" className="w-9 h-7 rounded object-cover border fl-border" loading="lazy" />)}
                      {photos.length > 3 && <span className="text-[11px] fl-muted">+{photos.length - 3}</span>}
                      {!photos.length && <span className="fl-muted inline-flex items-center gap-1 text-[11px]"><Camera className="w-3.5 h-3.5" /> 0</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 border-t fl-border whitespace-nowrap">
                    <p className={dateTone(c.next_service_date)}>{fmtDate(c.next_service_date)}</p>
                    {c.next_service_km != null && <p className={`text-[10px] ${c.mileage != null && c.next_service_km - c.mileage <= 1000 ? "fl-warn" : "fl-muted"}`}>při {fmtKm(c.next_service_km)}</p>}
                  </td>
                  <td className={`px-3 py-2.5 border-t fl-border whitespace-nowrap ${dateTone(c.stk_date)}`}>{fmtDate(c.stk_date)}</td>
                  <td className={`px-3 py-2.5 border-t fl-border whitespace-nowrap ${dateTone(c.insurance_date)}`}>{fmtDate(c.insurance_date)}</td>
                  <td className={`px-3 py-2.5 border-t fl-border whitespace-nowrap ${dateTone(c.vignette_date)}`}>{fmtDate(c.vignette_date)}</td>
                  <td className="px-3 py-2.5 border-t fl-border whitespace-nowrap">
                    <p className="fl-text-2">{TIRES[c.tires] || "—"}</p>
                    {c.tires_date && <p className={`text-[10px] ${dateTone(c.tires_date)}`}>přezout {fmtDate(c.tires_date)}</p>}
                  </td>
                  {isStaff && <td className="px-3 py-2.5 border-t fl-border whitespace-nowrap fl-text-2">{fmtCZK(c.expenses_total)}</td>}
                  {isStaff && <td className="px-3 py-2.5 border-t fl-border whitespace-nowrap fl-muted text-xs">{c.partner_name || "—"}</td>}
                  {!isStaff && (
                    <td className="px-3 py-2.5 border-t fl-border whitespace-nowrap" data-testid={`fleet-payout-${c.id}`}>
                      {c.partner_monthly != null && c.partner_monthly > 0 ? (
                        <><p className="fl-accent-text font-semibold">{Math.round(c.partner_monthly).toLocaleString("cs-CZ")} Kč</p><p className="text-[10px] fl-muted">{Math.round(c.partner_monthly / 1.21).toLocaleString("cs-CZ")} bez DPH</p></>
                      ) : <span className="fl-muted">—</span>}
                    </td>
                  )}
                  <td className="px-2 py-2.5 border-t fl-border fl-muted group-hover:fl-accent-text"><ChevronRight className="w-4 h-4" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11px] fl-muted border-t fl-border flex flex-wrap gap-4">
        <span><span className="fl-bad font-semibold">Červeně</span> = propadlé / překročené</span>
        <span><span className="fl-warn font-medium">Oranžově</span> = do 30 dní / do 1 000 km</span>
        <span>Kliknutím na řádek otevřete detail vozu s měsíční historií.</span>
      </div>
    </div>
  );
};

export default FleetTable;
